var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import * as PIXI from 'pixi.js';
import { difference, uniq } from 'lodash';
import { direction, forString } from "./Script";
var VERBOSE = false;
var FontAtlas = /** @class */ (function () {
    function FontAtlas(settings) {
        var _a, _b, _c, _d, _e, _f;
        this.fontLoader = undefined;
        this.fontSize = 48;
        this.resolution = 512;
        this.paddingInX = 6;
        this.paddingInY = 6;
        this.debugBounds = false;
        this.texture = [];
        this.textureId = 0;
        this.canvas = undefined;
        this.context = undefined;
        this.glyph = {};
        this.familyName = undefined;
        this.subfamilyName = undefined;
        this.scale = 1;
        this.ascent = 0;
        this.descent = 0;
        this.lineGap = 0;
        this.bbox = {
            minX: 0,
            maxX: 0,
            minY: 0,
            maxY: 0,
        };
        this.wordSpacing = 0;
        this.script = 'latn';
        this.direction = 'ltr';
        this.fillStyle = 'white';
        this.x = 0;
        this.y = 0;
        this.prevId = undefined;
        VERBOSE && console.log('create font atlas', settings);
        this.fontLoader = settings.fontLoader;
        this.fontSize = (_a = settings.fontSize) !== null && _a !== void 0 ? _a : this.fontSize;
        this.resolution = (_b = settings.resolution) !== null && _b !== void 0 ? _b : this.resolution;
        this.paddingInX = (_c = settings.paddingInX) !== null && _c !== void 0 ? _c : this.paddingInX;
        this.paddingInY = (_d = settings.paddingInY) !== null && _d !== void 0 ? _d : this.paddingInY;
        this.fillStyle = (_e = settings.fillStyle) !== null && _e !== void 0 ? _e : this.fillStyle;
        this.debugBounds = (_f = settings.debugBounds) !== null && _f !== void 0 ? _f : this.debugBounds;
    }
    Object.defineProperty(FontAtlas.prototype, "loaded", {
        /**
         * An Atlas is considered loaded when it has a canvas and its properties set.
         */
        get: function () {
            // this is more initialized than "loaded"
            return this.canvas && this.familyName;
        },
        enumerable: false,
        configurable: true
    });
    FontAtlas.prototype.update = function () {
        VERBOSE && console.log('update');
        if (!this.fontLoader.loaded()) {
            VERBOSE && console.log('unable to continue, font not loaded yet');
            return;
        }
        if (!this.canvas) {
            this._setupCanvas();
        }
        if (!this.familyName) {
            this._initProps();
        }
    };
    Object.defineProperty(FontAtlas.prototype, "font", {
        get: function () {
            if (!this.fontLoader.loaded()) {
                throw new Error('Font not loaded');
            }
            return this.fontLoader.font;
        },
        enumerable: false,
        configurable: true
    });
    FontAtlas.prototype._setupCanvas = function () {
        VERBOSE && console.log('setup canvas');
        this.canvas = document.createElement('canvas');
        this.canvas.width = this.resolution;
        this.canvas.height = this.resolution;
        this.context = this.canvas.getContext('2d');
        // background color
        // this.context.beginPath();
        // this.context.fillRect(0, 0, this.resolution, this.resolution);
        this.context.fillStyle = this.fillStyle;
        // this.context.fill();
        this.context.scale(1, -1);
    };
    FontAtlas.prototype._clearCanvas = function () {
        if (!this.context) {
            return;
        }
        this.context.clearRect(0, 0, this.canvas.width, -this.canvas.height);
    };
    FontAtlas.prototype._initProps = function () {
        VERBOSE && console.log('init props');
        var scale = this.fontSize / this.font.unitsPerEm;
        var ascent = this.font.ascent * scale;
        var descent = this.font.descent * scale;
        var lineGap = this.font.lineGap * scale;
        var minX = this.font.bbox.minX * scale;
        var minY = this.font.bbox.minY * scale;
        var maxX = this.font.bbox.maxX * scale;
        var maxY = this.font.bbox.maxY * scale;
        this.familyName = this.font.familyName;
        this.subfamilyName = this.font.subfamilyName;
        this.scale = scale;
        this.ascent = ascent;
        this.descent = descent;
        this.lineGap = lineGap;
        this.wordSpacing = this.fontSize * 0.25;
        this.bbox = {
            minX: minX,
            minY: minY,
            maxX: maxX,
            maxY: maxY
        };
    };
    /**
     * If we want to render (see) a glyph, we need a few things (data):
     * - rasterize a glyph into pixels so we can see it
     * - position data so we know how to position it
     * This method takes in a string and creates the necessary data for each character
     * @param string
     */
    FontAtlas.prototype.addGlyphsForString = function (string) {
        if (!this.loaded) {
            throw new Error('FontAtlas not loaded yet. Unable to add glyphs for string.');
        }
        // NOTE: we shouldn't be able to change this once this is set.
        this.script = forString(string);
        this.direction = direction(this.script);
        // Since Arabic uses ligatures, we can't save the individual glyphs since they appear different depending on the
        // neighbouring glyph; therefore, we're falling back to store the individual words.
        // TODO: try out some of the more common UNICODE_SCRIPTS to see if any other use ligatures
        // TODO: see if we can store out the individual glyphs. See Fontkit's layout.substitute.
        if (this.script === 'arab') {
            throw Error('Unsupported script "arab".');
        }
        else {
            string = string.replace(/\s+/g, '');
            var characters = [];
            for (var i = 0; i < string.length; i++) {
                characters.push(string.charAt(i));
            }
            characters = uniq(characters);
            this._addGlyphs(characters);
        }
    };
    FontAtlas.prototype._addGlyphs = function (string) {
        var _this = this;
        var missingGlyphs = this._missingGlyphs(__spreadArray([], string, true));
        if (missingGlyphs.length === 0) {
            return;
        }
        missingGlyphs.forEach(function (glyph) { return _this._addGlyph(glyph); });
        // eslint-disable-next-line
        this.texture[this.textureId] = PIXI.Texture.from(this.canvas);
    };
    FontAtlas.prototype._addGlyph = function (character) {
        VERBOSE && console.log('_addGlyph', character);
        var glyphId = this._glyphsForString(character);
        var glyph = this.font.getGlyph(glyphId);
        var bbox = glyph.bbox;
        var minX = bbox.minX * this.scale;
        var minY = bbox.minY * this.scale;
        var width = (bbox.maxX - bbox.minX) * this.scale;
        var height = (bbox.maxY - bbox.minY) * this.scale;
        var metrics = glyph._getMetrics();
        metrics.advanceWidth *= this.scale;
        metrics.advanceHeight *= this.scale;
        metrics.leftBearing *= this.scale;
        metrics.topBearing *= this.scale;
        this.context.save();
        if (this.prevId) {
            this.x += this.glyph[this.prevId].width + this.paddingInX;
            var nextPositionInX = this.x + width + this.paddingInX;
            // new line
            if (nextPositionInX > this.resolution) {
                this.x = this.paddingInX;
                this.y -= metrics.advanceHeight;
            }
            // new canvas/texture
            var nextPositionInY = this.y - metrics.advanceHeight;
            if (nextPositionInY < -this.resolution) {
                this.y = 0;
                this.textureId++;
                this._clearCanvas();
            }
        }
        var offsetX = -metrics.leftBearing + this.paddingInX + this.x;
        var offsetY = -metrics.advanceHeight + this.y;
        this.context.translate(offsetX, offsetY);
        this.context.beginPath();
        if (this.debugBounds) {
            this.context.rect(minX, minY, width, height);
            this.context.stroke();
        }
        glyph.render(this.context, this.fontSize);
        this.context.restore();
        this.prevId = character;
        this.glyph[character] = Object.assign({}, {
            x: offsetX + minX,
            y: offsetY + minY,
            width: width,
            height: height,
            textureId: this.textureId,
        }, metrics);
    };
    FontAtlas.prototype._glyphsForString = function (string) {
        // remove all whitespaces from string
        string = string.replace(/\s+/g, '');
        var glyphIds = [];
        var len = string.length;
        var idx = 0;
        var last = -1;
        var state = -1;
        while (idx <= len) {
            var code = 0;
            var nextState = 0;
            if (idx < len) {
                code = string.charCodeAt(idx++);
                if (code >= 0xd800 && code <= 0xdbff && idx < len) {
                    var next = string.charCodeAt(idx);
                    if (next >= 0xdc00 && next <= 0xdfff) {
                        idx++;
                        code = ((code & 0x3ff) << 10) + (next & 0x3ff) + 0x10000;
                    }
                }
                nextState = ((code >= 0xfe00 && code <= 0xfe0f) || (code >= 0xe0100 && code <= 0xe01ef)) ? 1 : 0;
            }
            else {
                idx++;
            }
            if (state === 0 && nextState === 1) {
                glyphIds.push(this.font._cmapProcessor.lookup(last, code));
            }
            else if (state === 0 && nextState === 0) {
                glyphIds.push(this.font._cmapProcessor.lookup(last));
            }
            last = code;
            state = nextState;
        }
        return glyphIds;
    };
    FontAtlas.prototype._missingGlyphs = function (glyphIds) {
        var necessaryIds = uniq(glyphIds);
        var storedIds = this._cachedIds();
        var missingIds = difference(necessaryIds, storedIds);
        return missingIds;
    };
    FontAtlas.prototype._cachedIds = function () {
        return Object.keys(this.glyph);
    };
    FontAtlas.prototype.getGlyphsForWord = function (word) {
        var _this = this;
        var glyphs = [];
        if (this.script === 'arab') {
            glyphs = [word];
        }
        else {
            word = word.replace(/\s+/g, '');
            for (var i = 0; i < word.length; i++) {
                glyphs.push(word.charAt(i));
            }
        }
        var storedGlyphs = glyphs.filter(function (glyph) { return _this.glyph.hasOwnProperty(glyph); });
        return storedGlyphs;
    };
    FontAtlas.prototype.destroy = function () {
        this.texture.forEach(function (texture) { return texture.destroy(true); });
        this.texture = [];
        this.textureId = 0;
        this._clearCanvas();
        this.glyph = {};
        this.familyName = undefined;
        this.subfamilyName = undefined;
        this.scale = 1;
        this.ascent = 0;
        this.descent = 0;
        this.wordSpacing = 0;
        this.script = 'latn';
        this.direction = 'ltr';
        this.x = 0;
        this.y = 0;
        this.prevId = undefined;
    };
    return FontAtlas;
}());
export { FontAtlas };
